/**
 * @fileoverview A component that provides a dropdown for selecting the status of a participant.
 * It uses the `@headlessui/react` library for the dropdown and transition.
 *
 * @example
 * <StatusDropdown current={States.review} onChange={handleStatusChange} />
 */

import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { States } from '../config'
import { RegularButton } from './elements/RegularButton'
import { Status, statusOptions } from './Status'


/**
 * @interface StatusDropdownProps
 * @description Props for the StatusDropdown component.
 * @property {States} current - The current status of the participant.
 * @property {string} name - The name of the dropdown.
 * @property {boolean} omitCurrent - Whether to omit the current status from the dropdown.
 * @property {(value: States) => void} onChange - The function to call when the status is changed.
 */
interface StatusDropdownProps {
    onChange: (value: States) => void
    omitCurrent?: boolean
    name?: string
    current: States
}

/**
 * @function StatusDropdown
 * @description Component for providing a dropdown for selecting the status of a participant.
 * @param {StatusDropdownProps} props - The props for the StatusDropdown component.
 * @returns {JSX.Element | null} - The StatusDropdown component.
 */
export default function StatusDropdown({
    current,
    name,
    omitCurrent,
    onChange,
}: StatusDropdownProps) {
    const statuses = omitCurrent
        ? statusOptions.filter(({ value }) => value !== current)
        : statusOptions

    return (
        <div className="flex rounded-md z-10">
            <Listbox
                as="div"
                className="relative -ml-px block rounded-md shadow-sm w-full"
                value={current}
                onChange={onChange}
            >
                {current && (
                    <Listbox.Button
                        as={RegularButton}
                        name={name}
                        className="relative justify-between h-10 inline-flex w-full focus:border-transparent focus:ring-offset-0"
                    >
                        <Status status={current} />
                        <ChevronDownIcon
                            className="h-5 w-5 ml-2 fill-gray-900"
                            aria-hidden="true"
                        />
                    </Listbox.Button>
                )}

                <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Listbox.Options className="absolute review-dropdown left-0 z-10 mt-2 -mr-1 w-[312px] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="divide-y">
                            {statuses.map(({ name, value }) => (
                                <Listbox.Option
                                    key={name}
                                    className="w-full review-dropdown-item trigger hover:bg-gray-100"
                                    value={value}
                                >
                                    <div className="p-4">
                                        <Status status={value} />
                                    </div>
                                </Listbox.Option>
                            ))}
                        </div>
                    </Listbox.Options>
                </Transition>
            </Listbox>
        </div>
    )
}
